"use strict";

const formA11y = require("../components/formA11y");

/**
 * Constants for gift card messages
 * @type {Object}
 */
const giftCardConstants = Object.freeze({
    CASE_NAMES: {
        applied: "applied",
        checkBalance: "checkBalance"
    },
    CLASS_NAMES: {
        hiddenClassName: "d-none",
        giftCardAppliedClassName: "applied",
        messagesWrapperClassName: "gift-card-messages-wrapper",
        containerClassName: "gift-card-message",
        textElClassName: "text",
        balanceElClassName: "balance",
        errorClassName: "gift-card-error-message",
        additionalPaymentClassName: "additional-payment-message",
        appliedGiftCardInfoContainerClassName: "givex-applied-info",
        cardNumberClassName: "card-number",
        remainingBalanceClassName: "remaining-balance",
        additionalPmRequiredClassName: "additional-pm-required",
        messageItemClassName: "msg-item"
    }
});

/**
 * Method to clear input fields
 * @returns {void}
 */
const clearGiftCardInputFields = () => {
    const cardNumberInput = document.getElementById("gift-card-number");
    const pinInput = document.getElementById("gift-card-pin");

    if (!cardNumberInput || !pinInput) return;

    cardNumberInput.value = "";
    pinInput.value = "";
};

/**
 * The two gift card inputs, as DOM nodes that are actually on the page.
 * @returns {Array<HTMLElement>} the card number and PIN inputs
 */
const giftCardInputs = () => ["gift-card-number", "gift-card-pin"]
    .map(id => document.getElementById(id))
    .filter(Boolean);

/**
 * Handles the balance message element
 * @param {Object} data - the data object containing textMsg and balanceMsg
 * @param {HTMLElement} container - the container element
 * @returns {void}
 */
const handleBalanceMsgElement = (data, container) => {
    if (!data || !container) return;

    const { textMsg, balanceMsg } = data;
    const {
        textElClassName,
        balanceElClassName
    } = giftCardConstants.CLASS_NAMES;
    const textEl = container.querySelector(`:scope .${textElClassName}`);
    const balanceEl = container.querySelector(`:scope .${balanceElClassName}`);

    if (!textEl || !balanceEl) return;

    // apply text and balance messages
    textEl.innerText = textMsg;
    balanceEl.innerText = balanceMsg;
    handleWrapperElVisibility();
};

/**
 * Method to handle applied gift card info
 * @param {Object} data - object containing the messages data
 * @param {HTMLElement} container - applied gift cards element
 * @returns {void}
 */
const handleAppliedGiftCardInfo = (data, container) => {
    if (!data || !container) return;

    const {
        cardNumberClassName,
        remainingBalanceClassName,
        additionalPmRequiredClassName,
        messageItemClassName,
        giftCardAppliedClassName,
        messagesWrapperClassName,
        containerClassName
    } = giftCardConstants.CLASS_NAMES;

    const {
        cardNumberMessage,
        remainingBalanceMsg,
        additionalPaymentMsg
    } = data;
    // get all message elements
    const messageElementsCollection = container.querySelectorAll(`:scope .${messageItemClassName}`);

    if (!messageElementsCollection.length) return;

    messageElementsCollection.forEach(element => {
        // add the additional messages for the applied gift cards
        // add card number
        if (element.classList.contains(cardNumberClassName) && cardNumberMessage) {
            element.innerText = cardNumberMessage;
        }
        // add remaining balance message
        if (element.classList.contains(remainingBalanceClassName) && remainingBalanceMsg) {
            element.innerText = remainingBalanceMsg;
        }
        // add additional payment required message
        if (element.classList.contains(additionalPmRequiredClassName) && additionalPaymentMsg) {
            element.innerText = additionalPaymentMsg;
            element.classList.add(giftCardAppliedClassName);
        }
    });

    const wrapperEl = container.closest(`.${messagesWrapperClassName}`);

    if (!wrapperEl) return;

    const messageEl = wrapperEl.querySelector(`.${containerClassName}`);

    if (!messageEl) return;

    // add applied class name
    container.classList.add(giftCardAppliedClassName);
    messageEl.classList.add(giftCardAppliedClassName);

    handleWrapperElVisibility();
    // clear input fields
    clearGiftCardInputFields();
};

/**
 * Method to handle wrapper element visibility
 * @returns {void}
 */
const handleWrapperElVisibility = () => {
    const {
        messagesWrapperClassName,
        hiddenClassName
    } = giftCardConstants.CLASS_NAMES;

    const wrapperEl = document.querySelector(`.${messagesWrapperClassName}`);

    if (!wrapperEl) return;

    wrapperEl.classList.remove(hiddenClassName);
};

/**
 * Method to handle error message
 * @param {Object} data - object containing the messages data
 * @param {HTMLElement} container - messages container element
 * @returns {void}
 */
const handleError = (data, container) => {
    if (!data || !container) return;

    const { errorClassName, textElClassName } = giftCardConstants.CLASS_NAMES;
    const textEl = container.querySelector(`:scope .${textElClassName}`);

    if (!textEl) return;

    container.classList.add(errorClassName);
    textEl.innerText = data.errorMsg;
    handleWrapperElVisibility();

    // The message reports on the card number and PIN together, so both are flagged and
    // both already point at this container. Focus goes to the first, which is what makes a
    // screen reader read the message out - nothing here sets `is-invalid`, so none of the
    // form-submit paths would otherwise reach it. After handleWrapperElVisibility, because
    // aria-describedby ignores a hidden target.
    const inputs = giftCardInputs();

    inputs.forEach(input => formA11y.markInvalid(input));
    formA11y.focusControl(inputs[0]);
};

/**
 * Method to clear class names
 * @param {HTMLElement} container - element to clear class names from
 * @param {Array} classNamesCollection - array of class names to remove
 * @returns {void}
 */
const clearClassNames = (container, classNamesToRemoveCollection) => {
    if (!container || !classNamesToRemoveCollection || !classNamesToRemoveCollection.length) return;

    container.classList.remove(...classNamesToRemoveCollection);
};

/**
 * Method to clear message element
 * @param {HTMLElement} container - message element
 * @returns {void}
 */
const clearMessageElement = (container) => {
    if (!container) return;

    const {
        giftCardAppliedClassName,
        errorClassName,
        messageItemClassName,
        additionalPmRequiredClassName
    } = giftCardConstants.CLASS_NAMES;
    // select all children elements
    const messageElementsCollection = container.querySelectorAll(`:scope .${messageItemClassName}`);
    const additionalPmMessageEl = container.querySelector(`:scope .${additionalPmRequiredClassName}`);
    const classNamesToClearCollection = [
        giftCardAppliedClassName,
        errorClassName
    ];

    if (!messageElementsCollection.length) return;

    // clear content
    messageElementsCollection.forEach(element => element.innerText = "");

    clearClassNames(container, classNamesToClearCollection);
    clearClassNames(additionalPmMessageEl, classNamesToClearCollection);
};

/**
 * Method to handle gift card messages
 * @param {Object} data - object containing the messages data
 * @param {string} caseName
 * @returns {void}
 */
const handleGiftCardMessage = (data, caseName) => {
    if (!data) return;

    const {
        applied,
        checkBalance
    } = giftCardConstants.CASE_NAMES;
    const {
        containerClassName,
        appliedGiftCardInfoContainerClassName
    } = giftCardConstants.CLASS_NAMES;

    const messageContainerEl = document.querySelector(`.${containerClassName}`);
    const appliedGivexContainer = document.querySelector(`.${appliedGiftCardInfoContainerClassName}`);


    clearMessageElement(appliedGivexContainer);
    clearMessageElement(messageContainerEl);
    giftCardInputs().forEach(input => formA11y.clearInvalid(input));

    switch (caseName) {
        case checkBalance:
            handleBalanceMsgElement(data, messageContainerEl);
            break;
        case applied:
            // set applied gift card message
            handleBalanceMsgElement(data, messageContainerEl);
            // add further information for applied gift cards
            handleAppliedGiftCardInfo(data, appliedGivexContainer);
            break;
        default:
            // default case is error
            handleError(data, messageContainerEl);
            // show element
    }
};

/**
 * Handles Givex generic error
 * @param {string} errorMsg - the error message
 * @returns {void}
 * @throws {Error} - if generic error message or message container element is missing
 */
const handleGivexGenericError = (errorMsg) => { /* eslint-disable-line no-unused-vars */ /* left for debugging */
    const {
        messagesWrapperClassName,
        containerClassName
    } = giftCardConstants.CLASS_NAMES;
    const wrapperEl = document.querySelector(`.${messagesWrapperClassName}`);
    const genericErrorMessage = wrapperEl?.getAttribute("data-generic-error-message");
    const messageContainerEl = wrapperEl?.querySelector(`.${containerClassName}`);

    if (!genericErrorMessage || !messageContainerEl) return;

    clearMessageElement(messageContainerEl);
    handleError({ errorMsg: errorMsg || genericErrorMessage }, messageContainerEl);
};

/**
* Handles the display of the removed Givex notification modal and optionally clears related elements.
*
* @param {boolean} isShow - Determines whether the notification modal should be shown.
* @returns {void}
*/
const handleRemovedGivexNotification = (isShow) => {
    if (!isShow) return;

    const $removedGivexNotificationModalEl = $(".givex-removed-notification-modal");
    const checkoutStage = document.querySelector(".data-checkout-stage")?.getAttribute("data-checkout-stage");

    if (!$removedGivexNotificationModalEl.length) return;

    $removedGivexNotificationModalEl.modal("show");
    removeAppliedGivexCardElements();

    $removedGivexNotificationModalEl.find(".confirm-btn").off("click").on("click", () => {
        const hasAdyenPaymentsBlock = document.querySelector(".payment-options-block .credit-card-selection-new") !== null;
        const isCheckoutPage = document.getElementById("checkout-main") !== null;

        // if givex covers full amount prior to removing it and payment methods block has been removed,
        // reload page to reload the adyen script and re-enable the payment methods block
        if (isCheckoutPage && !hasAdyenPaymentsBlock) {
            location.reload();
            return;
        }

        if (checkoutStage !== "payment") {
            return;
        }

        // attach event to the confirm button in order to
        // direct focus to gift card fields only for payment stage on checkout
        const $giftCardAccordion = $(".gift-card-toggler");

        if (!$giftCardAccordion.length) return;

        if (!$giftCardAccordion.hasClass("active")) {
            $giftCardAccordion.trigger("click");
        }

        $("html, body").animate({
            scrollTop: $giftCardAccordion.offset().top
        }, 500);
    });
};

/**
* Removes all applied gift card messages and clears the applied gift cards container.
* This function targets specific DOM elements to reset the state of gift card messages
* and applied gift cards in the checkout process.
*
* @function
* @returns {void}
*/
function removeAppliedGivexCardElements() {
    const $giftCardMessagesContainer = $(".gift-card-messages-wrapper");
    const $appliedGiftCardsContainer = $(".applied-gift-cards-wrapper");
    const $giftCartsDiscountWrapper = $(".gift-cards-wrapper");

    if ($giftCartsDiscountWrapper.length) {
        $giftCartsDiscountWrapper.empty();
    }

    if ($giftCardMessagesContainer.length) {
        $giftCardMessagesContainer.find(".msg-item").empty();
        $giftCardMessagesContainer.find(".applied").removeClass("applied");
    }

    if ($appliedGiftCardsContainer.length) {
        $appliedGiftCardsContainer.empty();
    }
}

module.exports.handleGiftCardMessage = handleGiftCardMessage;
module.exports.handleGivexGenericError = handleGivexGenericError;
module.exports.giftCardConstants = giftCardConstants;
module.exports.handleRemovedGivexNotification = handleRemovedGivexNotification;
